import React from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

// CTV-780: the fee column info hovers on the publisher payout ledger. A
// header label with a small info glyph; hovering or focusing it shows a short
// explanation of exactly how that column is computed. Hover AND focus so the
// copy is reachable by keyboard; rendered only while open, and portaled to
// document.body with fixed coordinates so the table's overflow container
// (horizontal scroll on a narrow laptop) cannot clip it.

interface FeeInfoHoverProps {
  /** Also the accessible name of the trigger; hidden when `labelHidden`. */
  label: string;
  /** CTV-786: the header's sort button renders the label, so the hover is the glyph alone. */
  labelHidden?: boolean;
  /** One or two short sentences: the exact calculation, in the publisher's words. */
  content: string;
  testId: string;
}

const PANEL_WIDTH = 272;
const GAP = 6;

const FeeInfoHover: React.FC<FeeInfoHoverProps> = ({ label, labelHidden, content, testId }) => {
  const [open, setOpen] = React.useState(false);
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const id = React.useId();

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect && typeof window !== 'undefined') {
      // Right-aligned under the glyph, pulled left when it would leave the viewport.
      const left = Math.max(8, Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8));
      setCoords({ top: rect.bottom + GAP, left });
    } else {
      setCoords({ top: 0, left: 0 });
    }
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <span className="relative inline-flex items-center gap-0.5 normal-case whitespace-nowrap">
      {!labelHidden && <span className="uppercase">{label}</span>}
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex items-center text-slate-400 hover:text-slate-600 focus:text-slate-600 focus:outline-none"
        aria-label={`About ${label}`}
        aria-describedby={open ? id : undefined}
        data-testid={testId}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <Info className="w-3 h-3" aria-hidden="true" />
      </button>
      {open &&
        coords &&
        createPortal(
          <span
            id={id}
            role="tooltip"
            data-testid={`${testId}-content`}
            style={{ position: 'fixed', top: coords.top, left: coords.left, width: PANEL_WIDTH }}
            className="z-50 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-[12px] font-normal normal-case tracking-normal text-slate-700 shadow-lg"
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default FeeInfoHover;
